import { CloseOutlined, LockFilled } from '@ant-design/icons';
import { Button } from '@mm/components';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { AddonSettingsKeyType } from '@mm/state/interfaces/subscription';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  getCurrentSubscription,
  selectAccountDetails,
} from '@mm/state/selectors/subscriptionSelectors';
import { centsToDollars } from '@mm/utils';
import { Divider, Spin } from 'antd';
import { uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './AccountLockedModal.module.css';

export const AccountLockedModal = () => {
  const facebookPage = useSelector(activeFacebookPageSelector);
  const currentSubscription = useSelector(
    getCurrentSubscription(facebookPage?.id),
  );
  const accountDetails = useSelector(selectAccountDetails);
  const editBillingLink =
    accountDetails && accountDetails.account
      ? accountDetails.account.editBillingLink
      : '';

  const addonsUsage = Object.entries(
    currentSubscription?.addonUsage || {},
  ).filter(addon => (addon[1].cost || 0) > 0);

  const { closeModal } = useModalActions();
  const [busy, setBusy] = useState(false);

  const getFriendlyName = (type: AddonSettingsKeyType) => {
    switch (type) {
      case 'contactsManagement':
        return 'Contact Management Fee';
      case 'xrayLeads':
        return 'Contacts Captured';
      case 'mercuryLeads':
        return 'Consumer Directory Leads';
      case 'emailSends':
        return 'Email Sends';
      case 'smsSends':
        return 'SMS/MMS Sends';
      case 'socialSends':
        return 'Social Sends';
      default:
        return type;
    }
  };

  const { getCurrentSubscription: getSubscription } =
    useSubscriptionCRUDActions();

  const onUpdateBilling = () => {
    window.open(editBillingLink, '_blank');
    closeModal();
  };

  const openMailApplication = () => {
    window.location.href = 'mailto:cs@customers.ai';
  };

  const retriveCurrentSubscription = async () => {
    if (facebookPage?.id && facebookPage.subscription && !addonsUsage.length) {
      setBusy(true);
      await getSubscription(
        facebookPage.id,
        true,
        facebookPage?.subscription?.id,
        isBilling2023Subscription(facebookPage.subscription),
      );
      setBusy(false);
    }
  };

  useEffect(() => {
    retriveCurrentSubscription();
  }, []);

  return (
    <div className={styles.container}>
      <CloseOutlined
        className={styles.closeIcon}
        onClick={() => closeModal()}
      />
      <div className={styles.icon}>
        <LockFilled className={styles.lockIcon} />
      </div>

      <div className={styles.title}>
        Your account is locked
        <div className={styles.description}>
          To unlock please pay the outstanding bill
        </div>
      </div>

      <div className={styles.paymentContainer}>
        <div className={styles.paymentHeader}>Your pending payment is:</div>
        <Spin spinning={busy} className={styles.spin}>
          <div className={styles.paymentBreakdown}>
            <div className={styles.paymentItem}>
              <span>Platform Fee</span>
              <span className={styles.price}>
                $
                {centsToDollars(
                  currentSubscription?.nextInvoiceAmount?.subsPrice || 0,
                )}
              </span>
            </div>
            {addonsUsage.map(item => (
              <div className={styles.paymentItem} key={uniqueId()}>
                <span>{getFriendlyName(item[0] as AddonSettingsKeyType)}</span>
                <span className={styles.price}>
                  ${centsToDollars(Number(item[1].cost))}
                </span>
              </div>
            ))}
          </div>
        </Spin>
        <Divider className={styles.divider} />
        <div className={styles.paymentItem}>
          <div className={styles.paymentHeader}>Total</div>
          <span className={styles.price}>
            $
            {centsToDollars(
              currentSubscription?.currentInvoiceAmount?.sum || 0,
            )}
          </span>
        </div>
      </div>

      <div className={styles.buttons}>
        {accountDetails && (
          <Button onClick={onUpdateBilling} css={{ container: styles.button }}>
            Update Payment Details
          </Button>
        )}
        <Button onClick={openMailApplication} color="transparent-no-shadow">
          Contact Support
        </Button>
      </div>
    </div>
  );
};
